/**
 * BottomNavMobile — Barre de navigation fixe en bas sur mobile uniquement.
 *
 * 5 onglets : Accueil, Recherche, Favoris, Publier, Compte.
 * WORLD-D-02 — Badges de notification :
 *   • Favoris : nombre de favoris (silencieux si 0)
 *   • Compte  : notifications non-lues (badge rouge)
 *
 * Les badges sont chargés uniquement si l'utilisateur est authentifié
 * (localStorage.user présent), pour éviter d'inutiles requêtes 401.
 */
import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Home, Search, Heart, PlusCircle, User } from 'lucide-react';
import apiClient from '../utils/api';

const tabs = [
  { key: 'home',     to: '/', icon: Home, label: 'Accueil', exact: true },
  { key: 'search',   to: '/properties', icon: Search, label: 'Recherche' },
  { key: 'favoris',  to: '/dashboard?tab=favoris', icon: Heart, label: 'Favoris', badgeKind: 'favorites' },
  { key: 'publish',  to: '/dashboard?publish=1', icon: PlusCircle, label: 'Publier' },
  { key: 'account',  to: '/dashboard?tab=profil', icon: User, label: 'Compte', badgeKind: 'unread' },
];

const isActive = (pathname, search, tab) => {
  if (tab.exact) return pathname === tab.to;
  if (tab.to.includes('?')) {
    const [tabPath, tabQuery] = tab.to.split('?');
    if (pathname !== tabPath) return false;
    const sp = new URLSearchParams(search);
    const want = new URLSearchParams(tabQuery);
    for (const [k, v] of want.entries()) {
      if (sp.get(k) !== v) return false;
    }
    return true;
  }
  return pathname === tab.to || pathname.startsWith(tab.to + '/');
};

const isLoggedIn = () => {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return false;
    const u = JSON.parse(raw);
    return !!(u && u.token);
  } catch { return false; }
};

const formatBadge = (n) => {
  if (!n || n <= 0) return null;
  return n > 99 ? '99+' : String(n);
};

const BottomNavMobile = () => {
  const { pathname, search } = useLocation();
  const [badges, setBadges] = useState({ favorites: 0, unread: 0 });

  useEffect(() => {
    // Refresh badges on route change (cheap — parallel + short cache in axios layer)
    let cancelled = false;
    if (!isLoggedIn()) { setBadges({ favorites: 0, unread: 0 }); return; }
    (async () => {
      try {
        const [favRes, notifRes] = await Promise.allSettled([
          apiClient.get('/favorites'),
          apiClient.get('/notifications/me/unread-count'),
        ]);
        if (cancelled) return;
        const favCount = favRes.status === 'fulfilled'
          ? (Array.isArray(favRes.value?.data) ? favRes.value.data.length : (favRes.value?.data?.count || 0))
          : 0;
        const unread = notifRes.status === 'fulfilled'
          ? (notifRes.value?.data?.unread_count || 0)
          : 0;
        setBadges({ favorites: favCount, unread });
      } catch { /* silent — badges are enhancement, not core */ }
    })();
    return () => { cancelled = true; };
  }, [pathname]);

  // Hide on pages that have their own bottom navigation to avoid stacking
  // Dashboard has MobileBottomTabBar (agent tabs), admin/pro have their own sidebars.
  const hasOwnBottomNav = ['/dashboard', '/admin', '/pro-dashboard'].some((p) => pathname.startsWith(p));
  // Hide on auth/payment pages for distraction-free flows
  if (['/auth', '/payment/success', '/payment/cancel'].some((p) => pathname.startsWith(p)) || hasOwnBottomNav) {
    return null;
  }

  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur-xl border-t border-stone-200 pb-[env(safe-area-inset-bottom)]"
      data-testid="bottom-nav-mobile"
      aria-label="Navigation mobile"
    >
      <div className="grid grid-cols-5 h-16">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = isActive(pathname, search, tab);
          const badge = tab.badgeKind ? formatBadge(badges[tab.badgeKind]) : null;
          const badgeTone = tab.badgeKind === 'unread' ? 'bg-red-500' : 'bg-brand-500';
          return (
            <NavLink
              key={tab.to}
              to={tab.to}
              className={`relative flex flex-col items-center justify-center gap-0.5 transition-colors ${
                active ? 'text-brand-600' : 'text-stone-500 hover:text-stone-900'
              }`}
              data-testid={`bottom-nav-${tab.label.toLowerCase()}`}
              aria-label={badge ? `${tab.label} — ${badge} nouveau${badge !== '1' ? 'x' : ''}` : tab.label}
            >
              <div className="relative">
                <Icon className="h-5 w-5" strokeWidth={active ? 2.4 : 1.8} />
                {badge && (
                  <span
                    className={`absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 flex items-center justify-center rounded-full text-label font-bold text-white ${badgeTone} ring-2 ring-white`}
                    data-testid={`bottom-nav-${tab.label.toLowerCase()}-badge`}
                  >
                    {badge}
                  </span>
                )}
              </div>
              <span className={`text-label ${active ? 'font-bold' : 'font-medium'}`}>{tab.label}</span>
              {active && <span className="absolute top-0 h-0.5 w-8 bg-brand-600 rounded-full" />}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomNavMobile;
